import React, { useRef, useState, useEffect } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	CircularProgress,
	Alert,
} from '@mui/material';
import {
	CheckCircle as CheckCircleIcon,
	Description as DescriptionIcon,
} from '@mui/icons-material';
// @ts-ignore
import WebViewer from '@pdftron/webviewer';
import { useToastContext } from '../../../core/components/Toast/context/ToastContext';

// Unified document interface for attestation
export interface AttestableDocument {
	documentId: string | number;
	documentName: string;
}

interface ManagerAttestationModalProps {
	open: boolean;
	onClose: () => void;
	document: AttestableDocument | null;
	onAttest: (documentId: string | number, attestedFile: File) => Promise<void>;
	documentUrl: string | null;
}

export function ManagerAttestationModal({
	open,
	onClose,
	document,
	onAttest,
	documentUrl,
}: ManagerAttestationModalProps): JSX.Element {
	const viewer = useRef<HTMLDivElement>(null);
	const instance = useRef<any>(null);
	const isInitializing = useRef<boolean>(false);
	
	const [isViewerReady, setIsViewerReady] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);
	const [error, setError] = useState<string | null>(null);
	
	const { showToast } = useToastContext();

	// Initialize WebViewer with signature tools only
	useEffect(() => {
		console.log('WebViewer init useEffect triggered - open:', open, 'documentUrl:', documentUrl, 'viewer.current:', !!viewer.current, 'instance.current:', !!instance.current, 'isInitializing:', isInitializing.current);
		
		if (!open || !documentUrl) {
			console.log('Conditions not met for initialization');
			return;
		}

		// Use a small timeout to ensure the ref is attached to the DOM
		const initTimer = setTimeout(() => {
			if (viewer.current && !instance.current && !isInitializing.current) {
				isInitializing.current = true;
				console.log('Initializing WebViewer with document:', documentUrl);
			
			WebViewer(
				{
					path: `/webviewer`,
					licenseKey: 'demo:1770900361581:6082a73403000000008bfdc578db599d0904b024b2339e144c1e745ec5',
					initialDoc: documentUrl,
					fullAPI: true,
					disableStreaming: true,
				} as any,
				viewer.current
			)
				.then((inst) => {
					console.log('WebViewer instance created successfully');
					instance.current = inst;
					
					// Configure UI immediately to hide unwanted menus
					try {
						console.log('Configuring UI - hiding unwanted toolbar groups');
						
						// Hide specific toolbar groups instead of disabling all ribbons
						inst.UI.disableElements([
							'toolbarGroup-View',
							'toolbarGroup-Annotate',
							'toolbarGroup-Shapes',
							'toolbarGroup-Insert',
							'toolbarGroup-Measure',
							'toolbarGroup-Edit',
							'toolbarGroup-Forms',
							'viewControlsButton',
							'searchButton',
							'menuButton',
							'leftPanelButton',
							'toggleNotesButton',
						]);
						
						// Enable and show only Fill and Sign
						inst.UI.enableElements([
							'toolbarGroup-FillAndSign',
						]);
						
						console.log('UI elements configured');
					} catch (err) {
						console.error('Error configuring UI:', err);
					}
					
					// Set viewer as ready immediately so loader disappears
					setIsViewerReady(true);
					console.log('Viewer marked as ready');
					
					inst.Core.documentViewer.addEventListener('documentLoaded', () => {
						console.log('Document loaded successfully');
						
						// Use setTimeout to ensure DOM is ready
						setTimeout(() => {
							try {
								// Enable Fill and Sign toolbar elements
								inst.UI.enableElements([
									'toolbarGroup-FillAndSign',
									'signatureToolButton',
									'freeHandToolButton',
									'freeTextToolButton',
								]);
								console.log('Fill and Sign elements enabled');
								
								// Set toolbar group to Fill and Sign
								inst.UI.setToolbarGroup('toolbarGroup-FillAndSign');
								console.log('Toolbar group set to Fill and Sign');
								
								// Set initial tool to signature
								inst.UI.setToolMode('AnnotationCreateSignature');
								console.log('Tool mode set to signature');
								
								// Set zoom to 100%
								inst.UI.setZoomLevel(1);
								console.log('Zoom set to 100%');
							} catch (err) {
								console.error('Error configuring viewer after document load:', err);
							}
						}, 500);
					});
					
					// Handle document load errors
					inst.Core.documentViewer.addEventListener('documentLoadError', (err) => {
						console.error('Document load error:', err);
						setError('Failed to load document. Please check the file and try again.');
					});
				})
				.catch((err) => {
					console.error('WebViewer initialization error:', err);
					setError('Failed to initialize document viewer');
					isInitializing.current = false;
					setIsViewerReady(true); // Show viewer anyway to display error
				});
			} else {
				console.log('Cannot initialize - viewer.current:', !!viewer.current, 'instance.current:', !!instance.current, 'isInitializing:', isInitializing.current);
			}
		}, 100); // Wait 100ms for the ref to attach

		return () => {
			// Cleanup on unmount
			clearTimeout(initTimer);
			console.log('Cleaning up WebViewer instance');
			if (instance.current) {
				instance.current.UI.dispose();
				instance.current = null;
				isInitializing.current = false;
			}
		};
	}, [open, documentUrl]);

	// Reset state when modal opens/closes
	useEffect(() => {
		if (open) {
			console.log('Modal opened - resetting state');
			setError(null);
			setIsProcessing(false);
		} else {
			console.log('Modal closed - cleaning up');
			// Clean up viewer when modal closes
			if (instance.current) {
				instance.current.UI.dispose();
				instance.current = null;
				isInitializing.current = false;
				setIsViewerReady(false);
			}
		}
	}, [open]);

	const handleAttestDocument = async () => {
		console.log('handleAttestDocument called');
		
		if (!document || !instance.current) {
			console.error('Missing document or instance:', { document: !!document, instance: !!instance.current });
			setError('Missing required information for attestation');
			return;
		}

		const { annotationManager, documentViewer } = instance.current.Core;
		const annotations = annotationManager.getAnnotationsList();
		console.log('Annotations found:', annotations.length);
		
		// Check if there are any annotations (signatures/drawings)
		if (annotations.length === 0) {
			console.warn('No annotations found on document');
			setError('Please add your signature or annotation to the document before attesting');
			showToast('error', 'Please sign the document before attesting');
			return;
		}

		setIsProcessing(true);
		setError(null);

		try {
			const doc = documentViewer.getDocument();
			if (!doc) {
				throw new Error('No document loaded');
			}

			console.log('Exporting annotations...');
			// Export the document with all annotations embedded
			const xfdfString = await annotationManager.exportAnnotations();
			console.log('Getting file data with flattened annotations...');
			const data = await doc.getFileData({ 
				xfdfString,
				flatten: true, // Flatten annotations into the PDF
			});
			
			console.log('Creating attested file...');
			const arr = new Uint8Array(data);
			const attestedFile = new File([arr], `${document.documentName}_attested.pdf`, {
				type: 'application/pdf',
			});

			console.log('Calling onAttest handler...');
			// Call the parent's attest handler with only the attested file
			await onAttest(document.documentId, attestedFile);

			console.log('Document attested successfully');
			showToast('success', 'Document attested successfully');
			onClose();
		} catch (err) {
			console.error('Attestation error:', err);
			setError('Failed to attest document. Please try again.');
			showToast('error', 'Failed to attest document');
		} finally {
			setIsProcessing(false);
		}
	};

	if (!document) {
		return <></>;
	}

	return (
		<Dialog
			open={open}
			onClose={isProcessing ? undefined : onClose}
			maxWidth="lg"
			fullWidth
			PaperProps={{
				sx: { minHeight: '85vh' },
			}}
		>
			<DialogTitle>
				<Box display="flex" alignItems="center" gap={1}>
					<DescriptionIcon />
					<Typography variant="h6">Attest Document: {document.documentName}</Typography>
				</Box>
			</DialogTitle>

			<DialogContent>
				<Alert severity="info" sx={{ mb: 2 }}>
					<Typography variant="body2">
						<strong>Instructions:</strong> Please add your signature or annotations to the document using the tools in the viewer. 
						Your signature will be permanently embedded in the document. Click &quot;Attest Document&quot; when complete.
					</Typography>
				</Alert>

				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				<Box
					sx={{
						height: '65vh',
						width: '100%',
						border: '1px solid #ddd',
						borderRadius: 1,
						overflow: 'hidden',
						position: 'relative',
					}}
				>
					{!isViewerReady && (
						<Box
							display="flex"
							alignItems="center"
							justifyContent="center"
							height="100%"
							sx={{
								position: 'absolute',
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								bgcolor: 'rgba(255, 255, 255, 0.9)',
								zIndex: 10,
							}}
						>
							<Box textAlign="center">
								<CircularProgress />
								<Typography variant="body2" sx={{ mt: 2 }}>
									Loading document viewer...
								</Typography>
							</Box>
						</Box>
					)}
					<div ref={viewer} style={{ height: '100%', width: '100%' }} />
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={onClose} disabled={isProcessing}>
					Cancel
				</Button>
				<Box sx={{ flex: '1 1 auto' }} />
				<Button
					variant="contained"
					onClick={handleAttestDocument}
					disabled={isProcessing || !isViewerReady}
					startIcon={isProcessing ? <CircularProgress size={20} /> : <CheckCircleIcon />}
					sx={{
						backgroundColor: 'rgb(68, 99, 136)',
						'&:hover': { backgroundColor: 'rgb(58, 89, 126)' },
					}}
				>
					{isProcessing ? 'Attesting...' : 'Attest Document'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
